import {
  getSearchDirectionConfig,
  getWordSearchFunction,
  getPhraseMatchFunction,
  isPersonalName,
  prepareForComparison,
  splitPhraseTokens,
  stripSpecialSymbols,
} from './search_core.js';
import {
  COUNTRY_OR_PROVINCE_OPTIONS,
  resolveCountryProvinceValues,
  SWEDISH_AREA_CODES,
} from './index_country_province.js';

export {
  getSearchDirectionConfig,
  getWordSearchFunction,
  getPhraseMatchFunction,
  isPersonalName,
  normalizeWhitespace,
  prepareForComparison,
  splitPhraseTokens,
  stripSpecialSymbols,
} from './search_core.js';

/*
This file contains code to do search in the inscriptions
*/

// Standard comparison operators that can be used both in QueryBuilderParser and custom search functions.
// String-based operators accept an optional third argument `ignoreCase` (default false).
export const operators = {
  equal: (a, b, ignoreCase) => ignoreCase ? String(a).toLowerCase() == String(b).toLowerCase() : a == b,
  not_equal: (a, b, ignoreCase) => ignoreCase ? String(a).toLowerCase() != String(b).toLowerCase() : a != b,
  in: (a, b) => b.includes(a),
  not_in: (a, b) => !b.includes(a),
  less: (a, b) => a < b,
  less_or_equal: (a, b) => a <= b,
  greater: (a, b) => a > b,
  greater_or_equal: (a, b) => a >= b,
  between: (a, b) => b[0] <= a && a <= b[1],
  not_between: (a, b) => !(b[0] <= a && a <= b[1]),
  begins_with: (a, b, ignoreCase) => prepareForComparison(a, ignoreCase).startsWith(prepareForComparison(b, ignoreCase)),
  not_begins_with: (a, b, ignoreCase) => !prepareForComparison(a, ignoreCase).startsWith(prepareForComparison(b, ignoreCase)),
  contains: (a, b, ignoreCase) => prepareForComparison(a, ignoreCase).includes(prepareForComparison(b, ignoreCase)),
  not_contains: (a, b, ignoreCase) => !prepareForComparison(a, ignoreCase).includes(prepareForComparison(b, ignoreCase)),
  ends_with: (a, b, ignoreCase) => prepareForComparison(a, ignoreCase).endsWith(prepareForComparison(b, ignoreCase)),
  not_ends_with: (a, b, ignoreCase) => !prepareForComparison(a, ignoreCase).endsWith(prepareForComparison(b, ignoreCase)),
  is_empty: (a) => a === '' || a === null || a === undefined || (Array.isArray(a) && a.length === 0),
  is_not_empty: (a) => a !== '' && a !== null && a !== undefined && (!Array.isArray(a) || a.length > 0),
  is_null: (a) => a === null || a === undefined,
  is_not_null: (a) => a !== null && a !== undefined,
};

const NEGATED_STRING_OPERATORS = new Set([
  'not_equal',
  'not_in',
  'not_begins_with',
  'not_contains',
  'not_ends_with',
]);

function getSearchValues(record, field) {
  const values = [record[field]];
  const fieldAliases = record.__searchAliases && record.__searchAliases[field];
  if (Array.isArray(fieldAliases)) {
    fieldAliases.forEach(value => values.push(value));
  }

  const seen = new Set();
  return values.filter(value => {
    const key = `${typeof value}:${String(value)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * JavaScript processor for jQuery QueryBuilder rules.
 * This module allows searching through data using rules
 * generated by jQuery QueryBuilder's getRules() function.
 */

export class QueryBuilderParser {
  /**
   * Initialize the parser with standard operators
   */
  constructor(customSearchFunctions = {}) {
    // Use the external operators
    this.operators = { ...operators };
    this.customSearchFunctions = customSearchFunctions;
  }

  /**
   * Apply rules from jQuery QueryBuilder to filter a list of data.
   *
   * @param {Object} rules - Rules object from jQuery QueryBuilder's getRules() method
   * @param {Array} data - Array of objects (records) to filter
   * @returns {Array} Filtered list of records with match details
   */
  parseRules(rules, data) {
    if (!rules || !data) {
      return [];
    }

    // If rules are invalid, return empty array
    if (rules.valid === false) {
      return [];
    }

    const result = [];
    // If data is an array, process each item
    if (Array.isArray(data)) {
      data.forEach(item => {
        const resultWithDetails = this._evaluateGroup(rules, item);
        if (resultWithDetails.match) {
          result.push({
            record: item,
            matchDetails: resultWithDetails.details || null
          });
        }
      });
    }
    // If data is any iterable (e.g. a generator, Set, etc.)
    else if (typeof data[Symbol.iterator] === 'function') {
      for (const item of data) {
        const resultWithDetails = this._evaluateGroup(rules, item);
        if (resultWithDetails.match) {
          result.push({
            record: item,
            matchDetails: resultWithDetails.details || null
          });
        }
      }
    }
    // Fallback: if data implements the iterator protocol (has a next() method)
    else if (typeof data.next === 'function') {
      let nextItem = data.next();
      while (!nextItem.done) {
        const resultWithDetails = this._evaluateGroup(rules, nextItem.value);
        if (resultWithDetails.match) {
          result.push({
            record: nextItem.value,
            matchDetails: resultWithDetails.details || null
          });
        }
        nextItem = data.next();
      }
    } else {
      throw new Error('Data is not iterable. It must be an array or implement the iterator protocol.');
    }

    return result;
  }

  /**
   * Recursively evaluate a group of rules.
   *
   * @param {Object} group - Group object with condition and rules
   * @param {Object} record - Data record to evaluate against
   * @returns {Object} Object with match (boolean) and details (object) properties
   * @private
   */
  _evaluateGroup(group, record) {
    // Helper function to apply negation if needed
    const applyNegation = (result, shouldNegate) => {
      if (!shouldNegate) return result;

      return {
        match: !result.match
      };
    };

    if (!group.condition || !group.rules) {
      return applyNegation({ match: false }, group.not);
    }

    const condition = group.condition.toUpperCase();
    const rules = group.rules;

    if (!rules || !rules.length) {
      return applyNegation({ match: true }, group.not);
    }

    const results = rules.map(rule => {
      // If it's a nested group
      if (rule.rules) {
        return this._evaluateGroup(rule, record);
      }
      // It's a rule
      else if (rule.id && rule.operator) {
        return this._evaluateRule(rule, record);
      }
      return { match: false };
    });

    // Combine the results based on the condition
    let combinedResult = null;

    if (condition === 'AND') {
      const isMatch = results.every(result => result.match);
      // For AND condition, we collect all details
      const combinedDetails = isMatch ?
        results.reduce((acc, result) => {
          if (result.details) {
            Object.entries(result.details).forEach(([field, details]) => {
              if (field === 'fieldRanges' && details && typeof details === 'object') {
                // Merge per-field substring ranges, deduping identical [start, end] pairs.
                if (!acc.fieldRanges) acc.fieldRanges = {};
                Object.entries(details).forEach(([fname, ranges]) => {
                  if (!Array.isArray(ranges)) return;
                  const existing = acc.fieldRanges[fname] || [];
                  const seen = new Set(existing.map(r => `${r[0]},${r[1]}`));
                  const merged = existing.slice();
                  ranges.forEach(r => {
                    const key = `${r[0]},${r[1]}`;
                    if (!seen.has(key)) {
                      seen.add(key);
                      merged.push(r);
                    }
                  });
                  acc.fieldRanges[fname] = merged;
                });
              } else if (!acc[field]) acc[field] = details;
              else if (Array.isArray(acc[field]) && Array.isArray(details)) {
                // Merge arrays and remove duplicates
                acc[field] = [...new Set([...acc[field], ...details])];
              }
            });
          }
          return acc;
        }, {}) : null;

      combinedResult = {
        match: isMatch,
        details: combinedDetails
      };
    } else if (condition === 'OR') {
      const matchingResults = results.filter(result => result.match);
      const isMatch = matchingResults.length > 0;

      // For OR condition, we take the first matching details
      combinedResult = {
        match: isMatch,
        details: isMatch ? matchingResults[0].details : null
      };
    } else {
      throw new Error(`Unknown condition: ${condition}`);
    }

    return applyNegation(combinedResult, group.not);
  }

  /**
   * Evaluate a single rule against a record.
   *
   * @param {Object} rule - Rule object with id, operator, and value
   * @param {Object} record - Data record to check against
   * @returns {Object} Object with match (boolean) and details (object) properties
   * @private
   */
  _evaluateRule(rule, record) {
    const field = rule.field || rule.id;
    const operatorName = rule.operator;
    const isMultiFieldRule = rule.data && rule.data.multiField === true;
    const ignoreCase = !!rule.ignoreCase;

    // Fast path: handle custom search functions first
    if (rule.id &&
        this.customSearchFunctions[rule.id] &&
        typeof this.customSearchFunctions[rule.id][operatorName] === "function") {

      // For custom functions, provide field value or entire record as needed
      const valueToCheck = isMultiFieldRule || rule.id === 'inscription_id'
        ? record
        : record[field];
      const result = this.customSearchFunctions[rule.id][operatorName](valueToCheck, rule.value, rule);

      // Normalize the result to always have match and details properties
      return typeof result === 'object' && 'match' in result ?
        result : { match: Boolean(result) };
    }

    // For standard single-field rules, verify the field exists
    if (!isMultiFieldRule && !(field in record)) {
      return { match: false };
    }

    // For multi-field rules without custom handlers, we can't process with standard operators
    if (isMultiFieldRule) {
      return { match: false };
    }

    const fieldValue = record[field];

    // Handle special operators that don't need a value
    if (['is_empty', 'is_not_empty', 'is_null', 'is_not_null'].includes(operatorName)) {
      const result = this.operators[operatorName](fieldValue);
      return {
        match: result,
        details: result ? { [field]: true } : null
      };
    }

    // For regular operators, ensure we have a value to compare against
    const ruleValue = rule.value;
    if (ruleValue === null || ruleValue === undefined) {
      return { match: false };
    }

    if (typeof ruleValue === 'number' && fieldValue === '') {
      // Handle case where fieldValue is an empty string but ruleValue is a number
      return { match: false };
    }

    const searchValues = getSearchValues(record, field);
    const isNegatedOperator = NEGATED_STRING_OPERATORS.has(operatorName);
    const isMatch = isNegatedOperator
      ? searchValues.every(value => Boolean(this.operators[operatorName](value, ruleValue, ignoreCase)))
      : searchValues.some(value => Boolean(this.operators[operatorName](value, ruleValue, ignoreCase)));

    return {
      match: isMatch,
      details: isMatch ? { [field]: true } : null
    };
  }

  /**
   * Add a custom operator function.
   *
   * @param {string} name - Name of the operator
   * @param {Function} func - Function that takes two arguments (fieldValue, ruleValue) and returns an object with match and details properties
   */
  addOperator(name, func) {
    this.operators[name] = func;
  }

  /**
   * Add a custom type converter.
   *
   * @param {string} typeName - Name of the type
   * @param {Function} func - Function that converts a value to the specified type
   */
  addTypeConverter(typeName, func) {
    this.typeConverters[typeName] = func;
  }
}

// Custom search functions using the new interface
const searchViaList = (fieldValue, ruleValue) => {
  const items = ruleValue.split('|');
  const match = items.some(item => item === fieldValue);
  return { match };
};

const splitSignatureListValue = (ruleValue) => {
  if (Array.isArray(ruleValue)) {
    return ruleValue;
  }
  if (typeof ruleValue === 'string') {
    // Commas/newlines are user-facing separators for inscription IDs; pipe is
    // the internal/backwards-compatible separator.  Do not split on semicolon:
    // signatures such as "Sö Fv1986;218" use semicolon as part of the ID.
    return ruleValue.split(/[|,\n]+/).map(item => item.trim()).filter(Boolean);
  }
  return [ruleValue];
};

/**
 * Wrapper function for searching in signature_text and aliases
 * @param {Object} record - The complete record object containing signature_text and aliases
 * @param {string|Array} ruleValue - The value to search for
 * @param {Function} operatorFn - The comparison function to apply (e.g., String.includes, String.startsWith)
 * @param {boolean} [negate=false] - Whether to negate the result
 * @returns {Object} Result object with match property
 */
const searchSignatureWrapper = (record, ruleValue, operatorFn, negate = false, ignoreCase = false) => {
  // Get the main signature text
  const signatureText = record.signature_text;

  // Get the aliases (if any) as an array
  const aliases = record.aliases ?
    record.aliases.split('|').map(a => a.trim()).filter(a => a) :
    [];

  // Combine into one array of values to check
  const allSignatures = [signatureText, ...aliases];

  // Process the rule value based on the input type
  const items = splitSignatureListValue(ruleValue);

  // Apply the operator function to check if any signature matches any item
  let match = allSignatures.some(sig =>
    items.some(item => operatorFn(sig, item, ignoreCase))
  );

  // Negate the result if needed
  if (negate) {
    match = !match;
  }

  return { match };
};

const doWordSearch = (entry, ruleValue, searchDirection, searchMode, ignoreCase = false, includeSpecialSymbols = false) => {
  const isAHit = getWordSearchFunction(searchMode, { ignoreCase, includeSpecialSymbols });
  const phraseMatcher = getPhraseMatchFunction(searchMode, { ignoreCase, includeSpecialSymbols });
  // key names defined in valueGetter, e.g. normalization_norse_to_transliteration
  const normalisationQuery = ruleValue['normalization'];
  const transliterationQuery = ruleValue['transliteration'];
  const namesMode = ruleValue['names_mode'];

  const searchDirectionConfig = getSearchDirectionConfig(searchDirection);
  if (!searchDirectionConfig) {
    return { match: false, details: null };
  }

  const normalizationField = searchDirectionConfig.normalisationField;

  const normalWords = entry[`${normalizationField}_words`];
  const transliterationWords = entry['transliteration_words'];

  const hasEffectiveToken = (query) => {
    if (query === null || query === undefined) return false;
    const tokens = splitPhraseTokens(String(query));
    if (tokens.length === 0) return false;
    if (includeSpecialSymbols) return tokens.some(token => token.length > 0);
    return tokens.some(token => stripSpecialSymbols(token).length > 0);
  };

  // If symbols are excluded and the query is made only of stripped symbols,
  // prevent accidental "match everything" behavior via includes('').
  if (normalisationQuery && !hasEffectiveToken(normalisationQuery)) {
    return { match: false, details: null };
  }
  if (transliterationQuery && !hasEffectiveToken(transliterationQuery)) {
    return { match: false, details: null };
  }

  const transliterationHasSymbol = (query) => {
    if (query === null || query === undefined) return false;
    return /(&quot;|&lt;|&gt;|["<>|[\](){}^\u00b4?+:\u00d7\u00b7\u00a4'÷¶])/.test(String(query));
  };

  const getRawTransliterationMatchRanges = (query) => {
    if (query === null || query === undefined) return [];

    const rawSource = String(entry.transliteration || '');
    const preparedSource = prepareForComparison(rawSource, ignoreCase);
    // Canonicalize query the same way phrase searches do:
    // trim outer whitespace and collapse internal whitespace to single spaces.
    // This ensures symbol contains-search also catches symbols at string edges.
    const preparedQuery = prepareForComparison(
      splitPhraseTokens(String(query)).join(' '),
      ignoreCase
    );
    if (preparedQuery.length === 0) return [];

    switch (searchMode) {
      case 'exact':
        return preparedSource === preparedQuery ? [[0, rawSource.length]] : [];
      case 'beginsWith':
        return preparedSource.startsWith(preparedQuery) ? [[0, preparedQuery.length]] : [];
      case 'endsWith': {
        if (!preparedSource.endsWith(preparedQuery)) return [];
        const start = preparedSource.length - preparedQuery.length;
        return [[start, start + preparedQuery.length]];
      }
      case 'includes':
      default: {
        const ranges = [];
        let cursor = 0;
        while (cursor <= preparedSource.length - preparedQuery.length) {
          const start = preparedSource.indexOf(preparedQuery, cursor);
          if (start === -1) break;
          ranges.push([start, start + preparedQuery.length]);
          cursor = start + preparedQuery.length;
        }
        return ranges;
      }
    }
  };

  // Build matching windows for a single query against a word list.
  // Returns Map<startIndex, number[]> where value is the window of indices.
  // For single-word queries, windows are of length 1.
  const findWindows = (words, query) => {
    const result = new Map();
    if (!query || !Array.isArray(words)) return result;
    const tokens = splitPhraseTokens(query);
    if (tokens.length === 0) return result;
    if (tokens.length > 1) {
      const windows = phraseMatcher(words, query);
      windows.forEach(w => result.set(w[0], w));
    } else {
      words.forEach((word, i) => {
        if (isAHit(word, tokens[0])) {
          result.set(i, [i]);
        }
      });
    }
    return result;
  };

  // Determine whether a window passes the names-mode filter.
  // ANY-word semantics:
  //   excludeNames: reject if ANY word in window is a personal name
  //   namesOnly:    require at least ONE word in window to be a personal name
  const windowPassesNamesFilter = (indices) => {
    if (namesMode !== 'excludeNames' && namesMode !== 'namesOnly') return true;
    const hasPersonal = indices.some(i => i < normalWords.length && isPersonalName(normalWords[i]));
    if (namesMode === 'excludeNames') return !hasPersonal;
    return hasPersonal; // namesOnly
  };

  const matchedSet = new Set();
  let matchFound = false;

  const acceptWindow = (indices) => {
    if (!windowPassesNamesFilter(indices)) return;
    matchFound = true;
    indices.forEach(i => matchedSet.add(i));
  };

  const buildResultFromMatchedSet = (extraDetails = null) => {
    const matchedWords = [...matchedSet].sort((a, b) => a - b);
    const numFoundNames = matchedWords.reduce((acc, i) => (
      i < normalWords.length && isPersonalName(normalWords[i]) ? acc + 1 : acc
    ), 0);
    const details = matchFound ? {
      wordIndices: matchedWords,
      numPersonalNames: numFoundNames
    } : null;
    if (details && extraDetails && typeof extraDetails === 'object') {
      if (extraDetails.fieldRanges && typeof extraDetails.fieldRanges === 'object') {
        details.fieldRanges = { ...extraDetails.fieldRanges };
      }
    }
    return {
      match: matchFound,
      details
    };
  };

  // Symbol-aware transliteration search (raw-string mode):
  // When include symbols is enabled and transliteration query contains symbols,
  // allow matching against the raw transliteration string. If normalization query
  // is also present, require both to match but do NOT require same word index.
  const useRawTransliterationMode = includeSpecialSymbols && transliterationHasSymbol(transliterationQuery);
  if (useRawTransliterationMode) {
    const transliterationRanges = getRawTransliterationMatchRanges(transliterationQuery);
    const transliterationMatches = transliterationRanges.length > 0;
    if (!normalisationQuery) {
      return {
        match: transliterationMatches,
        details: transliterationMatches ? {
          fieldRanges: {
            transliteration: transliterationRanges
          }
        } : null
      };
    }
    findWindows(normalWords, normalisationQuery).forEach(win => acceptWindow(win));
    if (!transliterationMatches || !matchFound) {
      return { match: false, details: null };
    }
    return buildResultFromMatchedSet({
      fieldRanges: {
        transliteration: transliterationRanges
      }
    });
  }

  if (normalisationQuery && transliterationQuery) {
    // Combined case: both queries must match starting at the same index.
    // Highlighted indices are the union of both windows.
    const norseWindows = findWindows(normalWords, normalisationQuery);
    const translitWindows = findWindows(transliterationWords, transliterationQuery);
    norseWindows.forEach((norseWin, startIdx) => {
      const translitWin = translitWindows.get(startIdx);
      if (!translitWin) return;
      const union = new Set([...norseWin, ...translitWin]);
      acceptWindow([...union]);
    });
  } else if (normalisationQuery) {
    findWindows(normalWords, normalisationQuery).forEach(win => acceptWindow(win));
  } else if (transliterationQuery) {
    findWindows(transliterationWords, transliterationQuery).forEach(win => acceptWindow(win));
  }

  return buildResultFromMatchedSet();
};

const searchCrossForm = (crosses, ruleValue) => {
  if (!crosses || crosses.length === 0) {
    return { match: false };
  }

  const ruleIsCertain = parseInt(ruleValue.is_certain, 10);
  const searchForm = ruleValue.form;

  // Flatten the array structure and search through all elements
  for (const cross of crosses) {
    if (!Array.isArray(cross)) continue;

    for (const group of cross) {
      if (!Array.isArray(group)) continue;

      for (const element of group) {
        // Check if the form name matches
        if (element.name === searchForm) {
          // If certainty doesn't matter (option 2) or certainty matches
          if (ruleIsCertain === 2 || ruleIsCertain === element.isCertain) {
            return { match: true };
          }
        }
      }
    }
  }

  return { match: false };
};

const searchHasPersonalName = (numNames, ruleValue) => {
  const isMatch = ruleValue === 0 ? numNames === 0 : numNames > 0;
  return { match: isMatch };
};

/**
 * Build substring-range search functions for a free-text field (e.g. translations).
 * Each returned operator handler returns { match, details: { fieldRanges: { <field>: [[start,end], ...] } } }.
 * Ranges are indices into the original (un-escaped) field value and are used later for
 * rendering highlights. Honors `rule.ignoreCase` on the rule.
 *
 * @param {string} fieldName - Name of the record field (e.g. 'english_translation').
 * @returns {Object} Map of operator name -> handler function.
 */
function buildTranslationSearchFunctions(fieldName) {
  const buildMatch = (ranges) => ({
    match: true,
    details: { fieldRanges: { [fieldName]: ranges } }
  });

  const findAllRanges = (haystack, needle, ignoreCase) => {
    const ranges = [];
    if (!needle) return ranges;
    const h = ignoreCase ? haystack.toLowerCase() : haystack;
    const n = ignoreCase ? needle.toLowerCase() : needle;
    let from = 0;
    while (from <= h.length - n.length) {
      const idx = h.indexOf(n, from);
      if (idx === -1) break;
      ranges.push([idx, idx + n.length]);
      from = idx + n.length; // non-overlapping
    }
    return ranges;
  };

  return {
    contains: (fieldValue, ruleValue, rule) => {
      const value = fieldValue == null ? '' : String(fieldValue);
      const query = ruleValue == null ? '' : String(ruleValue);
      const ranges = findAllRanges(value, query, !!(rule && rule.ignoreCase));
      if (ranges.length === 0) return { match: false };
      return buildMatch(ranges);
    },
    begins_with: (fieldValue, ruleValue, rule) => {
      const value = fieldValue == null ? '' : String(fieldValue);
      const query = ruleValue == null ? '' : String(ruleValue);
      if (query.length === 0 || query.length > value.length) return { match: false };
      const ignoreCase = !!(rule && rule.ignoreCase);
      const a = ignoreCase ? value.slice(0, query.length).toLowerCase() : value.slice(0, query.length);
      const b = ignoreCase ? query.toLowerCase() : query;
      if (a !== b) return { match: false };
      return buildMatch([[0, query.length]]);
    },
    ends_with: (fieldValue, ruleValue, rule) => {
      const value = fieldValue == null ? '' : String(fieldValue);
      const query = ruleValue == null ? '' : String(ruleValue);
      if (query.length === 0 || query.length > value.length) return { match: false };
      const ignoreCase = !!(rule && rule.ignoreCase);
      const start = value.length - query.length;
      const a = ignoreCase ? value.slice(start).toLowerCase() : value.slice(start);
      const b = ignoreCase ? query.toLowerCase() : query;
      if (a !== b) return { match: false };
      return buildMatch([[start, value.length]]);
    },
    equal: (fieldValue, ruleValue, rule) => {
      const value = fieldValue == null ? '' : String(fieldValue);
      const query = ruleValue == null ? '' : String(ruleValue);
      const ignoreCase = !!(rule && rule.ignoreCase);
      const a = ignoreCase ? value.toLowerCase() : value;
      const b = ignoreCase ? query.toLowerCase() : query;
      if (a !== b) return { match: false };
      if (value.length === 0) return { match: true }; // nothing to highlight
      return buildMatch([[0, value.length]]);
    },
  };
}

const englishTranslationSearchFunctions = buildTranslationSearchFunctions('english_translation');
const swedishTranslationSearchFunctions = buildTranslationSearchFunctions('swedish_translation');

function mergeFieldRanges(target, incoming) {
  if (!incoming || typeof incoming !== 'object') {
    return;
  }

  Object.entries(incoming).forEach(([fieldName, ranges]) => {
    if (!Array.isArray(ranges)) {
      return;
    }

    const existing = target[fieldName] || [];
    const seen = new Set(existing.map(range => `${range[0]},${range[1]}`));
    const merged = existing.slice();
    ranges.forEach(range => {
      if (!Array.isArray(range) || range.length < 2) {
        return;
      }
      const key = `${range[0]},${range[1]}`;
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(range);
      }
    });
    target[fieldName] = merged;
  });
}

function countPersonalNamesByWordIndices(entry, wordIndices) {
  const normalWords = Array.isArray(entry.normalisation_norse_words) ? entry.normalisation_norse_words : [];
  return wordIndices.reduce((acc, index) => {
    if (index >= normalWords.length) {
      return acc;
    }
    return isPersonalName(normalWords[index]) ? acc + 1 : acc;
  }, 0);
}

function searchRunicTextsCombined(entry, ruleValue, operatorName, rule) {
  const operatorToSearchMode = {
    contains: 'includes',
    equal: 'exact',
    begins_with: 'beginsWith',
    ends_with: 'endsWith',
  };
  const searchMode = operatorToSearchMode[operatorName];
  if (!searchMode) {
    return { match: false };
  }

  const query = String(ruleValue || '').trim();
  if (!query) {
    return { match: false };
  }

  const ignoreCase = !!(rule && rule.ignoreCase);
  const includeSpecialSymbols = !!(rule && rule.includeSpecialSymbols);
  const wordsFromNorse = doWordSearch(
    entry,
    { normalization: query, transliteration: '', names_mode: 'includeAll' },
    'norseToTransliteration',
    searchMode,
    ignoreCase,
    includeSpecialSymbols
  );
  const wordsFromScandinavian = doWordSearch(
    entry,
    { normalization: query, transliteration: '', names_mode: 'includeAll' },
    'scandinavianToTransliteration',
    searchMode,
    ignoreCase,
    includeSpecialSymbols
  );
  const wordsFromTransliteration = doWordSearch(
    entry,
    { normalization: '', transliteration: query, names_mode: 'includeAll' },
    'norseToTransliteration',
    searchMode,
    ignoreCase,
    includeSpecialSymbols
  );
  const englishMatch = englishTranslationSearchFunctions[operatorName](entry.english_translation, query, rule);
  const swedishMatch = swedishTranslationSearchFunctions[operatorName](entry.swedish_translation, query, rule);

  const candidates = [
    wordsFromNorse,
    wordsFromScandinavian,
    wordsFromTransliteration,
    englishMatch,
    swedishMatch,
  ];

  const matchingCandidates = candidates.filter(candidate => candidate && candidate.match);
  if (matchingCandidates.length === 0) {
    return { match: false };
  }

  const wordIndexSet = new Set();
  const fieldRanges = {};
  matchingCandidates.forEach(candidate => {
    const details = candidate.details;
    if (!details || typeof details !== 'object') {
      return;
    }
    if (Array.isArray(details.wordIndices)) {
      details.wordIndices.forEach(index => wordIndexSet.add(index));
    }
    if (details.fieldRanges && typeof details.fieldRanges === 'object') {
      mergeFieldRanges(fieldRanges, details.fieldRanges);
    }
  });

  const mergedDetails = {};
  if (wordIndexSet.size > 0) {
    const wordIndices = Array.from(wordIndexSet).sort((a, b) => a - b);
    mergedDetails.wordIndices = wordIndices;
    mergedDetails.numPersonalNames = countPersonalNamesByWordIndices(entry, wordIndices);
  }
  if (Object.keys(fieldRanges).length > 0) {
    mergedDetails.fieldRanges = fieldRanges;
  }

  return {
    match: true,
    details: Object.keys(mergedDetails).length > 0 ? mergedDetails : null,
  };
}

function getSignatureAreaCode(signatureText) {
  const signature = String(signatureText || '').trim();
  if (!signature) return '';
  const firstSpace = signature.indexOf(' ');
  return firstSpace === -1 ? signature : signature.slice(0, firstSpace);
}

const searchCountryOrProvince = (entry, ruleValues) => {
  const signatureAreaCode = getSignatureAreaCode(entry['signature_text']);
  const normalizedRuleValues = resolveCountryProvinceValues(ruleValues);
  for (let i = 0; i < normalizedRuleValues.length; i++) {
    const ruleCode = String(normalizedRuleValues[i] || '').trim();
    if (!ruleCode) continue;
    if (ruleCode === 'all_sweden') {
      if (SWEDISH_AREA_CODES.has(signatureAreaCode)) {
        return { match: true };
      }
      const districts = ['Skåne', 'Halland', 'Blekinge'];
      for (let j = 0; j < districts.length; j++) {
        if (operators.contains(entry['district'], districts[j])) {
          return { match: true };
        }
      }
      continue;
    }
    if (signatureAreaCode === ruleCode) {
      return { match: true };
    }
  }
  return { match: false };
}

function searchCountryOrProvinceContains(entry, ruleValue, rule) {
  const query = prepareForComparison(ruleValue, !!(rule && rule.ignoreCase)).trim();
  if (!query) {
    return { match: false };
  }
  const signatureAreaCode = getSignatureAreaCode(entry['signature_text']);
  const matchingCodes = COUNTRY_OR_PROVINCE_OPTIONS
    .filter(option => {
      const text = prepareForComparison(option.text, !!(rule && rule.ignoreCase));
      const code = prepareForComparison(String(option.value || '').trim(), !!(rule && rule.ignoreCase));
      return text.includes(query) || code.includes(query);
    })
    .map(option => option.value);
  if (matchingCodes.length === 0) {
    return { match: false };
  }
  return searchCountryOrProvince(entry, matchingCodes);
}

function stripDiacritics(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function normalizeReferencesSearchValue(value, ignoreCase) {
  // References often mix plain URLs and human labels; fold diacritics so
  // "Fornvännen" can match "fornvannen.se" style URLs.
  return stripDiacritics(prepareForComparison(value, ignoreCase));
}

function searchReferencesCombined(fieldValue, ruleValue, operatorName, rule) {
  const ignoreCase = !!(rule && rule.ignoreCase);
  const value = normalizeReferencesSearchValue(fieldValue, ignoreCase);
  const query = normalizeReferencesSearchValue(ruleValue, ignoreCase);

  switch (operatorName) {
    case 'contains':
      return { match: value.includes(query) };
    case 'not_contains':
      return { match: !value.includes(query) };
    case 'begins_with':
      return { match: value.startsWith(query) };
    case 'not_begins_with':
      return { match: !value.startsWith(query) };
    case 'ends_with':
      return { match: value.endsWith(query) };
    case 'not_ends_with':
      return { match: !value.endsWith(query) };
    case 'equal':
      return { match: value === query };
    case 'not_equal':
      return { match: value !== query };
    default:
      return { match: false };
  }
}

const customSearchFunctions = {
  inscription_id: {
    in: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.equal, false, !!rule.ignoreCase),
    in_separated_list: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.equal, false, !!rule.ignoreCase),
    begins_with: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.begins_with, false, !!rule.ignoreCase),
    not_begins_with: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.begins_with, true, !!rule.ignoreCase),
    ends_with: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.ends_with, false, !!rule.ignoreCase),
    not_ends_with: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.ends_with, true, !!rule.ignoreCase),
    contains: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.contains, false, !!rule.ignoreCase),
    not_contains: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.contains, true, !!rule.ignoreCase),
    equal: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.equal, false, !!rule.ignoreCase),
    not_equal: (record, ruleValue, rule) => searchSignatureWrapper(record, ruleValue, operators.equal, true, !!rule.ignoreCase),
  },
  inscription_country: {
    in: searchCountryOrProvince,
    contains: searchCountryOrProvinceContains,
  },
  normalization_norse_to_transliteration: {
    contains: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'norseToTransliteration', 'includes', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    equal: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'norseToTransliteration', 'exact', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    begins_with: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'norseToTransliteration', 'beginsWith', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    ends_with: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'norseToTransliteration', 'endsWith', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
  },
  normalization_scandinavian_to_transliteration: {
    contains: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'scandinavianToTransliteration', 'includes', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    equal: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'scandinavianToTransliteration', 'exact', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    begins_with: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'scandinavianToTransliteration', 'beginsWith', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
    ends_with: (fieldValue, ruleValue, rule) => {
      return doWordSearch(fieldValue, ruleValue, 'scandinavianToTransliteration', 'endsWith', !!rule.ignoreCase, !!rule.includeSpecialSymbols);
    },
  },
  search_runic_texts: {
    contains: (fieldValue, ruleValue, rule) => searchRunicTextsCombined(fieldValue, ruleValue, 'contains', rule),
    equal: (fieldValue, ruleValue, rule) => searchRunicTextsCombined(fieldValue, ruleValue, 'equal', rule),
    begins_with: (fieldValue, ruleValue, rule) => searchRunicTextsCombined(fieldValue, ruleValue, 'begins_with', rule),
    ends_with: (fieldValue, ruleValue, rule) => searchRunicTextsCombined(fieldValue, ruleValue, 'ends_with', rule),
  },
  cross_form: {
    cross_form: searchCrossForm,
  },
  has_personal_name: {
    equal: searchHasPersonalName,
    not_equal: (fieldValue, ruleValue) => {
      const result = searchHasPersonalName(fieldValue, ruleValue);
      return { match: !result.match };
    },
  },
  english_translation: englishTranslationSearchFunctions,
  swedish_translation: swedishTranslationSearchFunctions,
  references_combined: {
    contains: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'contains', rule),
    not_contains: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'not_contains', rule),
    begins_with: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'begins_with', rule),
    not_begins_with: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'not_begins_with', rule),
    ends_with: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'ends_with', rule),
    not_ends_with: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'not_ends_with', rule),
    equal: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'equal', rule),
    not_equal: (fieldValue, ruleValue, rule) => searchReferencesCombined(fieldValue, ruleValue, 'not_equal', rule),
  },
};

const TRANSLATION_FIELDS = new Set(['english_translation', 'swedish_translation']);
const VALUELESS_OPERATORS = new Set(['is_empty', 'is_not_empty', 'is_null', 'is_not_null']);
const SYMBOL_AWARE_RULE_IDS = new Set([
  'normalization_norse_to_transliteration',
  'normalization_scandinavian_to_transliteration',
]);
const SPECIAL_SYMBOL_PATTERN = /(&quot;|&lt;|&gt;|["<>|[\](){}^\u00b4?+:\u00d7\u00b7\u00a4'÷¶])/;

function trimToken(token) {
  return String(token || '').replace(/^[\s"'`.,;:!?()[\]{}<>]+|[\s"'`.,;:!?()[\]{}<>]+$/g, '');
}

function collectTokensFromRuleValue(value, outputMap) {
  if (value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach(v => collectTokensFromRuleValue(v, outputMap));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach(v => collectTokensFromRuleValue(v, outputMap));
    return;
  }
  const tokens = splitPhraseTokens(String(value)).map(trimToken).filter(Boolean);
  tokens.forEach(token => {
    const key = token.toLowerCase();
    if (!outputMap.has(key)) {
      outputMap.set(key, token);
    }
  });
}

function traverseTranslationRules(group, outputMap) {
  if (!group || !Array.isArray(group.rules)) return;
  group.rules.forEach(rule => {
    if (rule && Array.isArray(rule.rules)) {
      traverseTranslationRules(rule, outputMap);
      return;
    }
    if (!rule || !TRANSLATION_FIELDS.has(rule.id)) return;
    if (VALUELESS_OPERATORS.has(rule.operator)) return;
    collectTokensFromRuleValue(rule.value, outputMap);
  });
}

export function getTranslationSearchTokens(rules) {
  const tokenMap = new Map();
  traverseTranslationRules(rules, tokenMap);
  return Array.from(tokenMap.values());
}

export function getTranslationSearchTokenCount(rules) {
  return getTranslationSearchTokens(rules).length;
}

export function getTranslationOccurrenceCount(searchResults) {
  if (!Array.isArray(searchResults)) {
    return 0;
  }

  let totalOccurrences = 0;
  for (const result of searchResults) {
    const rangesByField = result?.matchDetails?.fieldRanges;
    if (!rangesByField || typeof rangesByField !== 'object') {
      continue;
    }
    ['english_translation', 'swedish_translation'].forEach(fieldName => {
      const ranges = rangesByField[fieldName];
      if (Array.isArray(ranges)) {
        totalOccurrences += ranges.length;
      }
    });
  }

  return totalOccurrences;
}

function collectStringValues(value, output) {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) output.push(trimmed);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach(v => collectStringValues(v, output));
    return;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach(v => collectStringValues(v, output));
  }
}

function findRuleNeedingIncludeSymbols(group) {
  if (!group || !Array.isArray(group.rules)) return false;

  for (const rule of group.rules) {
    if (!rule) continue;
    if (Array.isArray(rule.rules)) {
      if (findRuleNeedingIncludeSymbols(rule)) return true;
      continue;
    }
    if (!SYMBOL_AWARE_RULE_IDS.has(rule.id)) continue;
    if (rule.includeSpecialSymbols) continue;
    if (VALUELESS_OPERATORS.has(rule.operator)) continue;

    const values = [];
    collectStringValues(rule.value, values);
    if (values.some(v => SPECIAL_SYMBOL_PATTERN.test(v))) {
      return true;
    }
  }

  return false;
}

export function shouldSuggestIncludeSymbols(rules) {
  return findRuleNeedingIncludeSymbols(rules);
}



/**
 * Perform a search using QueryBuilder rules
 * @param {Object} rules - Rules object from jQuery QueryBuilder's getRules() method
 * @param {Array|Iterable} dbMap - Data to search through
 * @returns {Array} Array of objects that match search rules. Each item includes record data from
 *                  dbMap and matchDetails property with details of the match.
 */
export function doSearch(rules, dbMap) {
  const parser = new QueryBuilderParser(customSearchFunctions);
  const resultsRaw = parser.parseRules(rules, dbMap);
  const results = resultsRaw.map(({ record, matchDetails }) => ({
    ...record,
    matchDetails
  }));

  return results;
}

export function calcWordsAndPersonalNames(dbMap) {
  let totalWordMatches = 0;
  let totalPersonalNames = 0;
  let totalSignatures = 0;
  let isWordSpecific = false;
  let isGeneral = false;

  try {
    const entries = dbMap.values();
    for (const entry of entries) {
      if (entry.matchDetails && entry.matchDetails.wordIndices) {
        totalWordMatches += entry.matchDetails.wordIndices.length;
        totalPersonalNames += entry.matchDetails.numPersonalNames;
        isWordSpecific = true;
      } else {
        totalWordMatches += entry.normalisation_norse_word_boundaries.length;
        entry.normalisation_norse_word_boundaries.forEach(boundary => {
          totalPersonalNames += boundary.isPersonal;
        });
        isGeneral = true;
      }
      totalSignatures++;
    }
  } catch (error) {
    console.error('Error calculating words and personal names:', error);
  }

  const countType = (isGeneral && isWordSpecific)
    ? 'mixed'
    : (isWordSpecific ? 'word-specific' : 'general');

  $(document).trigger('updateSignatureCount', { count: totalSignatures });
  $(document).trigger('updateWordCount', { count: totalWordMatches, type: countType });
  $(document).trigger('updatePersonalNameCount', { count: totalPersonalNames, type: countType });
}
